// =============================================================================
// SimSense Platform — Settings Page
// =============================================================================

import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useAuthStore } from "../state/authStore";
import { useDesktopStore } from "../state/desktopStore";
import { api } from "../utils/api";
import { useToast } from "../components/Toast";
import { ApiEntitlementLink, EntitlementLink, mapEntitlementLink } from "./settingsLinks";

export function Settings() {
  const { user, updatePreferences, checkAuth } = useAuthStore();
  const { 
    isTauri, 
    isWatching, 
    telemetryPath, 
    setTelemetryPath, 
    lastSuccess, 
    lastError, 
    queueCount,
    retryQueueCount,
    statusMessage,
    scanAndUpload
  } = useDesktopStore();
  const [saveStatus, setSaveStatus] = useState("");
  const [prefs, setPrefs] = useState<{ 
    shareSetups: boolean; 
    shareTelemetry: boolean; 
    units: "metric" | "standard";
    autoDeleteIbt: boolean;
    retentionMonths: number;
  }>({ 
    shareSetups: true, 
    shareTelemetry: true, 
    units: "standard",
    autoDeleteIbt: false,
    retentionMonths: 6
  });
  const toast = useToast();


  // Linked Accounts State
  const [links, setLinks] = useState<EntitlementLink[]>([]);
  const [_loadingLinks, setLoadingLinks] = useState(false);

  useEffect(() => {
    document.title = "Settings | SimSense";
    if (user) {
      fetchLinks();
    }
    if (user?.preferences) {
      setPrefs({
        shareSetups: user.preferences.shareSetups ?? true,
        shareTelemetry: user.preferences.shareTelemetry ?? true,
        units: (user.preferences.units as any) ?? "standard",
        autoDeleteIbt: user.preferences.autoDeleteIbt ?? false,
        retentionMonths: user.preferences.retentionMonths ?? 6
      });
    }
  }, [user]);

  const fetchLinks = async () => {
    try {
      setLoadingLinks(true);
      const data = await api<ApiEntitlementLink[]>("/api/ss/auth/links");
      setLinks(data.map(mapEntitlementLink));
    } catch (err) {
      console.error("Failed to fetch account links", err);
    } finally {
      setLoadingLinks(false);
    }
  };

  const handleSave = async () => {
    try {
      await updatePreferences(prefs);
      setSaveStatus("Settings saved successfully.");
    } catch (err) {
      setSaveStatus("Failed to save cloud preferences.");
    }
    setTimeout(() => setSaveStatus(""), 3000);
  };

  const handleBrowse = async () => {
    if (!isTauri) return;
    try {
      const { invoke } = await import("@tauri-apps/api/core");
      const path: string | null = await invoke("pick_telemetry_dir");
      if (path) {
        setTelemetryPath(path);
        const { remoteLog } = await import("../utils/logger");
        void remoteLog("User changed telemetry directory", "info", { newPath: path });
      }
    } catch (err) {
      console.error("Failed to pick directory", err);
    }
  };

  const handleCopyDiagnostics = () => {
    const diag = {
      isTauri,
      isWatching,
      telemetryPath,
      statusMessage,
      queueCount,
      lastSuccess,
      lastError,
      userAgent: navigator.userAgent,
      time: new Date().toISOString()
    };
    navigator.clipboard.writeText(JSON.stringify(diag, null, 2));
    toast.success("Diagnostics copied to clipboard.");
  };

  const handleUpgrade = async (planId: string) => {
    try {
      const data = await api<any>(`/api/ss/billing/checkout?planId=${planId}`, { method: "POST" });
      if (data.checkout_url) {
        window.open(data.checkout_url, "_blank");
      }
    } catch (err) {
      toast.error("Failed to start checkout. Please try again later.");
    }
  };

  const [searchParams, setSearchParams] = useSearchParams();
  const [iracingLinking, setIracingLinking] = useState(false);

  // Handle iRacing link callback result
  useEffect(() => {
    const linkResult = searchParams.get("iracing_link");
    if (linkResult === "success") {
      toast.success("iRacing account linked successfully!");
      checkAuth(true);
      setSearchParams({}, { replace: true });
    } else if (linkResult === "conflict") {
      toast.error("This iRacing account is already linked to another SimSense account.");
      setSearchParams({}, { replace: true });
    }
  }, [searchParams]);

  const handleLinkAccount = (provider: string) => {
    if (provider === "wix") {
      window.location.href = "/api/ss/auth/links/wix/login";
    }
  };

  const handleLinkIracing = () => {
    window.location.href = "/api/ss/auth/iracing/link";
  };

  const handleUnlinkIracing = async () => {
    if (!confirm("Are you sure you want to unlink your iRacing account? You will need to relink before you can access SimSense features.")) return;
    setIracingLinking(true);
    try {
      await api("/api/ss/auth/iracing/unlink", { method: "POST" });
      await checkAuth(true);
      toast.success("iRacing account unlinked.");
    } catch {
      toast.error("Failed to unlink iRacing account.");
    } finally {
      setIracingLinking(false);
    }
  };

  const handleUnlinkAccount = async (linkId: string) => {
    if (!confirm("Are you sure you want to unlink this account? You may lose access to premium features.")) return;
    try {
      await api(`/api/ss/auth/links/${linkId}`, { method: "DELETE" });
      setLinks(links.filter(l => l.id !== linkId));
      await checkAuth(true);
      toast.success("Account unlinked.");
    } catch (err) {
      toast.error("Failed to unlink account.");
    }
  };

  if (!user) return <div style={{ padding: "40px", textAlign: "center" }}>Please login to view settings.</div>;

  const oscLink = links.find(l => l.providerType === "wix");

  return (
    <div style={{ maxWidth: "800px", margin: "0 auto" }}>
      <h2 style={{ marginBottom: "24px" }}>Settings</h2>

      <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
        
        {/* Profile Link Card */}
        <Link to="/profile/me" style={{ textDecoration: "none", color: "inherit" }}>
          <section style={{
            background: "var(--card)",
            padding: "20px 24px",
            borderRadius: "var(--radius)",
            border: "1px solid var(--border)",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            transition: "border-color 0.2s",
            cursor: "pointer",
          }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = "var(--accent)")}
            onMouseLeave={e => (e.currentTarget.style.borderColor = "var(--border)")}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              {user.profilePictureUrl ? (
                <img src={user.profilePictureUrl} alt="Avatar" style={{ width: "48px", height: "48px", borderRadius: "12px", objectFit: "cover", border: "2px solid var(--accent)" }} />
              ) : (
                <div style={{ width: "48px", height: "48px", borderRadius: "12px", background: "linear-gradient(135deg, #3b82f6, #1d4ed8)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "18px", fontWeight: 800, color: "#fff" }}>
                  {(user.displayName || "U")[0].toUpperCase()}
                </div>
              )}
              <div>
                <div style={{ fontWeight: 600, fontSize: "15px" }}>{user.displayName || "Your Profile"}</div>
                <div style={{ fontSize: "12px", color: "var(--text3)" }}>View and edit your public profile, bio, and iRacing ID</div>
              </div>
            </div>
            <div style={{ color: "var(--text3)", fontSize: "18px" }}>→</div>
          </section>
        </Link>

        {/* Account Info */}
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>Account</h3>
          <div style={{ display: "grid", gridTemplateColumns: "120px 1fr", gap: "12px", fontSize: "14px" }}>
            <span style={{ color: "var(--text2)" }}>Email</span>
            <span style={{ fontWeight: 500 }}>{user.email || "N/A"}</span>
            
            <span style={{ color: "var(--text2)" }}>Plan</span>
            <span style={{ 
              fontWeight: 700, 
              color: user.plan === "pro" || user.capabilities.has_pro ? "var(--accent)" : user.plan === "admin" ? "#f59e0b" : "inherit",
              textTransform: "uppercase"
            }}>
              {user.plan} {user.capabilities.has_pro && user.plan === 'free' ? "(PRO via Link)" : ""}
            </span>

            <span style={{ color: "var(--text2)" }}>Member Since</span>
            <span style={{ color: "var(--text2)" }}>{new Date(user.createdAt).toLocaleDateString()}</span>
          </div>
        </section>

        {/* Linked Accounts [OT-204] */}
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>Linked Accounts</h3>
          <p style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "20px" }}>
            Link your accounts from partner shops to unlock premium setups and content.
          </p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "32px", height: "32px", background: "#fff", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center", color: "#000", fontWeight: 900, fontSize: "10px" }}>OSC</div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "14px" }}>Outlier Speed Co.</div>
                  <div style={{ fontSize: "12px", color: "var(--text3)" }}>
                    {oscLink ? (oscLink.isActive ? "Linked & Active" : "Link Inactive") : "Not linked"}
                  </div>
                </div>
              </div>
              {oscLink ? (
                <button 
                  onClick={() => handleUnlinkAccount(oscLink.id)}
                  style={{
                    padding: "6px 16px",
                    background: "none",
                    border: "1px solid #ef4444",
                    color: "#ef4444",
                    borderRadius: "var(--radius-sm)",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                >
                  Unlink
                </button>
              ) : (
                <button 
                  onClick={() => handleLinkAccount("wix")}
                  style={{
                    padding: "6px 16px",
                    background: "none",
                    border: "1px solid var(--border)",
                    color: "var(--text1)",
                    borderRadius: "var(--radius-sm)",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "12px"
                  }}
                >
                  Link Account
                </button>
              )}
            </div>

            {/* iRacing Account */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: "8px" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                <div style={{ width: "32px", height: "32px", background: "#0047AB", borderRadius: "4px", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
                    <circle cx="9" cy="9" r="7" stroke="#fff" strokeWidth="1.5"/>
                    <path d="M6 9h6M9 6v6" stroke="#fff" strokeWidth="1.5" strokeLinecap="round"/>
                  </svg>
                </div>
                <div>
                  <div style={{ fontWeight: 600, fontSize: "14px" }}>iRacing</div>
                  <div style={{ fontSize: "12px", color: user.iracingLinked ? "var(--good)" : "var(--text3)" }}>
                    {user.iracingLinked
                      ? `Linked to iRacing account ${user.iracingId || ""}`
                      : "Not linked"}
                  </div>
                </div>
              </div>
              {user.iracingLinked ? (
                <button
                  onClick={handleUnlinkIracing}
                  disabled={iracingLinking}
                  style={{
                    padding: "6px 16px",
                    background: "none",
                    border: "1px solid #ef4444",
                    color: "#ef4444",
                    borderRadius: "var(--radius-sm)",
                    fontWeight: 600,
                    cursor: iracingLinking ? "not-allowed" : "pointer",
                    fontSize: "12px",
                    opacity: iracingLinking ? 0.6 : 1,
                  }}
                >
                  {iracingLinking ? "Unlinking..." : "Unlink"}
                </button>
              ) : (
                <button
                  onClick={handleLinkIracing}
                  style={{
                    padding: "6px 16px",
                    background: "none",
                    border: "1px solid var(--border)",
                    color: "var(--text1)",
                    borderRadius: "var(--radius-sm)",
                    fontWeight: 600,
                    cursor: "pointer",
                    fontSize: "12px",
                  }}
                >
                  Link Account
                </button>
              )}
            </div>
          </div>
        </section>

        {/* Telemetry Config */}
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>Local Telemetry</h3>
          <p style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "16px" }}>
            Specify where the SimSense Agent should watch for new iRacing `.ibt` files.
          </p>
          
          <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            <label style={{ fontSize: "12px", fontWeight: 600, color: "var(--text2)" }}>iRacing Telemetry Path</label>
            <div style={{ display: "flex", gap: "8px" }}>
              <input 
                type="text" 
                value={telemetryPath}
                onChange={(e) => setTelemetryPath(e.target.value)}
                placeholder="C:/Users/Name/Documents/iRacing/telemetry"
                style={{
                  flex: 1,
                  padding: "10px 12px",
                  background: "rgba(0,0,0,0.2)",
                  border: "1px solid var(--border)",
                  borderRadius: "var(--radius-sm)",
                  color: "var(--text)",
                  fontSize: "14px",
                  fontFamily: "monospace"
                }}
              />
              {isTauri && (
                <button
                  onClick={handleBrowse}
                  style={{
                    padding: "0 16px",
                    background: "var(--card)",
                    border: "1px solid var(--border)",
                    color: "var(--text)",
                    borderRadius: "var(--radius-sm)",
                    fontSize: "13px",
                    cursor: "pointer"
                  }}
                >
                  Browse
                </button>
              )}
            </div>
          </div>

          <div style={{ marginTop: "24px", display: "flex", alignItems: "center", gap: "16px" }}>
            <button 
              onClick={handleSave}
              style={{
                padding: "10px 24px",
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--radius-sm)",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Save Settings
            </button>
            {saveStatus && <span style={{ fontSize: "13px", color: "#4ade80" }}>{saveStatus}</span>}
          </div>
        </section>

        {/* Desktop Agent Health & Recovery */}
        {isTauri && (
          <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "16px" }}>
              <h3 style={{ fontSize: "18px" }}>Desktop Agent Health</h3>
              <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                <div style={{ 
                  width: "8px", 
                  height: "8px", 
                  borderRadius: "50%", 
                  background: isWatching ? "var(--good)" : "var(--warn)",
                  boxShadow: isWatching ? "0 0 8px var(--good)" : "none"
                }} />
                <span style={{ fontSize: "12px", fontWeight: 600, textTransform: "uppercase", color: "var(--text2)" }}>{statusMessage}</span>
              </div>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px", marginBottom: "24px" }}>
              <div style={{ background: "rgba(0,0,0,0.2)", padding: "16px", borderRadius: "8px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: "11px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "8px" }}>Status & Path</div>
                <div style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "4px" }}>Watcher: <strong>{isWatching ? "Active" : "Stopped"}</strong></div>
                <div style={{ fontSize: "13px", color: "var(--text2)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }} title={telemetryPath}>
                  Path: <code style={{ fontSize: "11px", background: "rgba(255,255,255,0.05)", padding: "2px 4px", borderRadius: "2px" }}>{telemetryPath || "Not set"}</code>
                </div>
              </div>
              <div style={{ background: "rgba(0,0,0,0.2)", padding: "16px", borderRadius: "8px", border: "1px solid var(--border)" }}>
                <div style={{ fontSize: "11px", color: "var(--text3)", textTransform: "uppercase", marginBottom: "8px" }}>Activity</div>
                <div style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "4px" }}>Last Success: <span style={{ color: lastSuccess ? "var(--good)" : "inherit" }}>{lastSuccess || "None this session"}</span></div>
                <div style={{ fontSize: "13px", color: "var(--text2)" }}>
                  Queue: <strong>{queueCount}</strong> 
                  {retryQueueCount > 0 && <span style={{ marginLeft: "8px", color: "var(--warn)" }}>Retrying: <strong>{retryQueueCount}</strong></span>}
                </div>
              </div>
            </div>

            <div style={{ display: "flex", gap: "12px" }}>
              <button 
                onClick={() => void scanAndUpload()}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  color: "var(--text)",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                🔄 Run Recovery Scan
              </button>
              <button 
                onClick={handleCopyDiagnostics}
                style={{
                  flex: 1,
                  padding: "10px",
                  background: "var(--card)",
                  border: "1px solid var(--border)",
                  color: "var(--text)",
                  borderRadius: "var(--radius-sm)",
                  fontSize: "13px",
                  fontWeight: 600,
                  cursor: "pointer"
                }}
              >
                📋 Copy Diagnostics
              </button>
            </div>
          </section>
        )}

        {/* Display Units */}
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>Display Units</h3>
          <p style={{ fontSize: "13px", color: "var(--text2)", marginBottom: "20px" }}>
            Choose your preferred measurement system for telemetry and weather data.
          </p>
          
          <div style={{ display: "flex", gap: "12px" }}>
            <button
              onClick={() => setPrefs({ ...prefs, units: "standard" })}
              style={{
                flex: 1,
                padding: "12px",
                background: prefs.units === "standard" ? "var(--accent)" : "rgba(255,255,255,0.05)",
                border: "1px solid",
                borderColor: prefs.units === "standard" ? "var(--accent)" : "var(--border)",
                color: prefs.units === "standard" ? "#fff" : "var(--text2)",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.2s ease"
              }}
            >
              Standard (F°, mph)
            </button>
            <button
              onClick={() => setPrefs({ ...prefs, units: "metric" })}
              style={{
                flex: 1,
                padding: "12px",
                background: prefs.units === "metric" ? "var(--accent)" : "rgba(255,255,255,0.05)",
                border: "1px solid",
                borderColor: prefs.units === "metric" ? "var(--accent)" : "var(--border)",
                color: prefs.units === "metric" ? "#fff" : "var(--text2)",
                borderRadius: "8px",
                cursor: "pointer",
                fontWeight: 600,
                transition: "all 0.2s ease"
              }}
            >
              Metric (C°, km/h)
            </button>
          </div>

          <div style={{ marginTop: "16px", display: "flex", alignItems: "center", gap: "16px" }}>
            <button
              onClick={handleSave}
              style={{
                padding: "10px 24px",
                background: "var(--accent)",
                color: "#fff",
                border: "none",
                borderRadius: "var(--radius-sm)",
                fontWeight: 600,
                cursor: "pointer"
              }}
            >
              Save Preferences
            </button>
            {saveStatus && <span style={{ fontSize: "13px", color: "#4ade80" }}>{saveStatus}</span>}
          </div>
        </section>

        {/* Subscription */}
        <section style={{ background: "var(--card)", padding: "24px", borderRadius: "var(--radius)", border: "1px solid var(--border)" }}>
          <h3 style={{ fontSize: "18px", marginBottom: "16px" }}>Subscription</h3>
          {user.plan === "free" && !user.capabilities.has_pro ? (
            <div>
              <p style={{ fontSize: "14px", marginBottom: "20px" }}>You are currently on the Free tier. Upgrade to unlock advanced coaching, deeper telemetry analysis, and community setups.</p>
              
              <div style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", background: "rgba(255,255,255,0.03)", border: "1px solid var(--border)", borderRadius: "8px" }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: "15px" }}>SimSense Pro</div>
                    <div style={{ fontSize: "12px", color: "var(--text2)" }}>Unlimited Advanced Coaching & Telemetry Analysis</div>
                    <div style={{ fontSize: "13px", fontWeight: 700, color: "var(--accent)", marginTop: "4px" }}>$5/mo</div>
                  </div>
                  <button 
                    onClick={() => handleUpgrade("pro_monthly")}
                    style={{
                      padding: "8px 20px",
                      background: "var(--accent)",
                      color: "#fff",
                      border: "none",
                      borderRadius: "var(--radius-sm)",
                      fontWeight: 600,
                      cursor: "pointer",
                      fontSize: "13px"
                    }}
                  >
                    Upgrade Now
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div style={{ fontSize: "24px" }}>💎</div>
              <div>
                <div style={{ fontWeight: 600, color: "var(--text1)" }}>
                  SimSense {user.capabilities.is_admin ? "ADMIN" : user.capabilities.has_pro ? "PRO" : user.plan.toUpperCase()} Member
                </div>
                <div style={{ fontSize: "13px", color: "var(--text2)" }}>
                  {user.capabilities.has_pro && user.plan === "free" 
                    ? "Your Pro status is active via a linked partner account." 
                    : "Thank you for your support! You have full access to all features."}
                </div>
              </div>
            </div>
          )}
        </section>

      </div>
    </div>
  );
}

